import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Loader2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../lib/i18n';

/* ========================================================================= */
/* VOICE INPUT — VERSION 1                                                   */
/* ========================================================================= */
/* Architecture: Push-to-record -> Stop -> POST Blob to Cloudflare Worker    */
/* -> Extract Persian transcription -> Insert/Append into associated input   */
/*                                                                           */
/* Endpoint: https://voice-to-text.salim-zarepour.workers.dev/               */
/* Model: Cloudflare Workers AI (@cf/openai/whisper-large-v3-turbo, fa)      */
/* ========================================================================= */

const VOICE_WORKER_URL = 'https://voice-to-text.salim-zarepour.workers.dev/';

export interface VoiceInputButtonProps {
  /** Current text in the input field to allow safe appending */
  currentValue?: string;
  /** Callback to update the input field with either fresh or appended text */
  onUpdate?: (newValue: string) => void;
  /** Raw transcription callback */
  onTranscription?: (text: string) => void;
  /** Accessible title / tooltip */
  title?: string;
  /** Custom extra CSS classes */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
}

export const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  currentValue = '',
  onUpdate,
  onTranscription,
  title,
  className = '',
  disabled = false,
}) => {
  const { t } = useLanguage();
  const [recordingState, setRecordingState] = useState<'idle' | 'recording' | 'processing'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const errorTimeoutRef = useRef<number | null>(null);

  // Clean up timer and media tracks on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const showError = (msg: string) => {
    setErrorMessage(msg);
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    errorTimeoutRef.current = window.setTimeout(() => {
      setErrorMessage(null);
    }, 4500);
  };

  /**
   * Detect the best supported audio MIME type across different browsers
   */
  const getSupportedMimeType = (): string => {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
      return '';
    }

    const candidateTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
      'audio/mp4',
      'audio/aac',
    ];

    for (const type of candidateTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return '';
  };

  /**
   * Start native browser audio recording
   */
  const startRecording = async () => {
    if (disabled || recordingState !== 'idle') return;
    setErrorMessage(null);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      showError(t('voiceInputPermissionDenied', 'دسترسی به میکروفون داده نشد.'));
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const options: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks to release microphone hardware immediately
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        const rawMimeType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: rawMimeType });

        // If audio blob is empty or extremely short (< 150 bytes)
        if (audioBlob.size < 150) {
          setRecordingState('idle');
          showError(t('voiceInputNoSpeechDetected', 'صدایی برای تبدیل به متن تشخیص داده نشد.'));
          return;
        }

        await processAudioTranscription(audioBlob);
      };

      mediaRecorder.onerror = (e) => {
        console.error('MediaRecorder error event:', e);
        stopRecording();
        showError(t('voiceInputRecordingError', 'ضبط صدا با مشکل مواجه شد.'));
      };

      mediaRecorder.start(250); // Collect data chunks every 250ms
      setRecordingState('recording');
      setRecordingDuration(0);

      // Start duration counter
      timerRef.current = window.setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (err: unknown) {
      console.error('Microphone permission / capture error:', err);
      const isPermissionDenied =
        err instanceof Error &&
        (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');

      if (isPermissionDenied) {
        showError(t('voiceInputPermissionDenied', 'دسترسی به میکروفون داده نشد.'));
      } else {
        showError(t('voiceInputRecordingError', 'ضبط صدا با مشکل مواجه شد.'));
      }
      setRecordingState('idle');
    }
  };

  /**
   * Stop active recording and trigger processing
   */
  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.error('Error stopping MediaRecorder:', err);
      }
    }
  };

  /**
   * Send audio Blob directly to Cloudflare Speech-to-Text Worker
   */
  const processAudioTranscription = async (audioBlob: Blob) => {
    setRecordingState('processing');

    try {
      const response = await fetch(VOICE_WORKER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': audioBlob.type || 'audio/webm',
        },
        body: audioBlob,
      });

      if (!response.ok) {
        throw new Error(`Worker HTTP error with status ${response.status}`);
      }

      const data = await response.json();
      const transcriptionText: string = (data && typeof data.text === 'string' ? data.text : '').trim();

      if (!transcriptionText) {
        showError(t('voiceInputNoSpeechDetected', 'صدایی برای تبدیل به متن تشخیص داده نشد.'));
      } else {
        // Apply transcription to input according to specification:
        // If empty -> insert directly. If already has text -> append with a space.
        const currentTrimmed = (currentValue || '').trim();
        const updatedValue = currentTrimmed
          ? `${currentTrimmed} ${transcriptionText}`
          : transcriptionText;

        if (onUpdate) {
          onUpdate(updatedValue);
        }
        if (onTranscription) {
          onTranscription(transcriptionText);
        }
      }
    } catch (error) {
      console.error('Voice-to-Text Worker request failed:', error);
      showError(t('voiceInputTranscribeError', 'تبدیل صدا به متن انجام نشد. لطفاً دوباره تلاش کنید.'));
    } finally {
      setRecordingState('idle');
      setRecordingDuration(0);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (recordingState === 'idle') {
      startRecording();
    } else if (recordingState === 'recording') {
      stopRecording();
    }
  };

  // Format seconds to mm:ss
  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || recordingState === 'processing'}
        title={
          recordingState === 'recording'
            ? t('voiceInputStop', 'توقف ضبط')
            : recordingState === 'processing'
            ? t('voiceInputProcessing', 'در حال تبدیل صدا به متن...')
            : title || t('voiceInputNormal', 'ورودی صوتی')
        }
        aria-label={
          recordingState === 'recording'
            ? t('voiceInputStop', 'توقف ضبط')
            : recordingState === 'processing'
            ? t('voiceInputProcessing', 'در حال تبدیل صدا به متن...')
            : title || t('voiceInputNormal', 'ورودی صوتی')
        }
        className={`inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer select-none shrink-0 ${
          recordingState === 'recording'
            ? 'bg-red-500 hover:bg-red-600 text-white shadow-md shadow-red-500/30 animate-pulse ring-2 ring-red-300 dark:ring-red-900'
            : recordingState === 'processing'
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300 dark:border-amber-800 opacity-90 cursor-wait'
            : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300 dark:hover:bg-indigo-900/60 border border-indigo-200/80 dark:border-indigo-800/80'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {recordingState === 'recording' ? (
          <>
            <Square className="w-3.5 h-3.5 fill-current" />
            <span className="text-[11px] font-mono font-bold tracking-tight">
              {formatDuration(recordingDuration)}
            </span>
          </>
        ) : recordingState === 'processing' ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span className="text-[11px] hidden sm:inline">
              {t('voiceInputProcessingShort', 'تبدیل صدا...')}
            </span>
          </>
        ) : (
          <>
            <Mic className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
            <span className="text-[11px] hidden md:inline">
              {t('voiceInputBtnLabel', 'صدا')}
            </span>
          </>
        )}
      </button>

      {/* Floating Error Bubble */}
      {errorMessage && (
        <div className="absolute bottom-full mb-1.5 right-0 z-50 min-w-[220px] max-w-xs bg-red-600 text-white text-[11px] font-medium px-3 py-2 rounded-xl shadow-xl flex items-start gap-2 animate-fadeIn border border-red-400 dir-rtl text-right">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-200" />
          <span className="flex-1 leading-snug">{errorMessage}</span>
        </div>
      )}
    </div>
  );
};

export default VoiceInputButton;
